import { AppHeader } from '@/components/AppHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { FileText, AlertTriangle, Sparkles, X, ArrowLeft, Heart, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/SEO';

export default function TermsOfService() {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user ?? null);
    });
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Terms of Service - Therappi®"
        description="Read the Terms of Service for Therappi®, the whole-self mental wellness platform."
        path="/terms"
      />
      <AppHeader user={user} onSignOut={handleSignOut} />
      
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="sr-only">Therappi Terms of Service</h1>
        <Card>
          <CardHeader className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              aria-label="Close"
              className="absolute right-4 top-4 h-8 w-8 rounded-full"
            >
              <X className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2 mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(-1)}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </div>
            <CardTitle className="flex items-center gap-2 text-3xl">
              <FileText className="h-8 w-8" />
              Terms of Service
            </CardTitle>
            <p className="text-muted-foreground">Last updated: January 2025</p>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none dark:prose-invert">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing or using Therappi<sup>®</sup>, you agree to be bound by these Terms of Service and our Privacy Policy. 
                If you do not agree to these terms, please do not use our service.
              </p>
              <p className="mt-2">
                <strong>Company:</strong> Therappi Ltd, Thames House, 77-77a High Street, Esher, Surrey, KT10 9PA, United Kingdom
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">2. Service Description</h2>
              <p>
                Therappi<sup>®</sup> provides AI-powered therapeutic wellness support, including but not limited to:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Conversational AI reflection support (AI Therappi<sup>®</sup>)</li>
                <li>Mental health tracking and insights</li>
                <li>Nutrition and lifestyle monitoring</li>
                <li>Inflammatory Load (IL) scoring and analysis</li>
                <li>Integration with third-party wellness devices (WHOOP, Garmin)</li>
                <li>Crisis resources and safety planning tools</li>
                <li>Partner linking and relationship wellness features</li>
              </ul>
            </section>

            <section className="mb-8 bg-primary/10 p-6 rounded-lg border-2 border-primary/30">
              <h2 className="text-2xl font-semibold mb-4 text-primary flex items-center gap-2">
                <AlertTriangle className="h-6 w-6 text-therapeutic-gold" />
                3. Medical Disclaimer
              </h2>
              
              <h3 className="text-xl font-semibold mb-3">3.1 Not a Substitute for Professional Care</h3>
              <p className="font-semibold">
                Therappi<sup>®</sup> is a WELLNESS TOOL, not a medical service. It does NOT replace professional medical advice, diagnosis, or treatment.
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Always consult qualified healthcare providers for medical conditions</li>
                <li>Never disregard professional medical advice or delay seeking it because of information from our service</li>
                <li>If you are experiencing a medical emergency, call emergency services immediately</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">3.2 Inflammatory Load (IL) Score Disclaimer</h3>
              <p className="font-semibold">The IL Score is an EDUCATIONAL WELLNESS INDICATOR, NOT a medical diagnostic tool.</p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>What it IS:</strong> A lifestyle-based score reflecting potential inflammatory patterns in diet, sleep, stress, and activity</li>
                <li><strong>What it is NOT:</strong> A measurement of actual inflammatory markers in your blood (such as CRP, IL-6, or TNF-α)</li>
                <li><strong>Not validated for diagnosis:</strong> The IL score has NOT been clinically validated for diagnosing inflammatory conditions</li>
                <li><strong>Individual variation:</strong> Results may not reflect your actual physiological inflammation status</li>
                <li><strong>Requires medical confirmation:</strong> If concerned about inflammation, request laboratory blood tests from your doctor</li>
              </ul>
              <p className="font-semibold mt-4">
                DO NOT use the IL score to self-diagnose, start supplements, change medications, or make medical decisions without consulting a healthcare provider.
              </p>

              <h3 className="text-xl font-semibold mb-3">3.3 Crisis Situations</h3>
              <p>
                If you are experiencing suicidal thoughts, self-harm urges, or any crisis requiring immediate intervention:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>UK:</strong> Call 999 or text SHOUT to 85258</li>
                <li><strong>US:</strong> Call 988 (Suicide & Crisis Lifeline)</li>
                <li><strong>International:</strong> Visit <a href="https://findahelpline.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">findahelpline.com</a></li>
              </ul>
              <p>
                Our AI crisis detection is designed to provide resources but is NOT a substitute for human crisis intervention services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">4. User Responsibilities</h2>
              <p>You agree to:</p>
              <ul className="list-disc pl-6 mb-4">
                <li>Provide accurate information about your age, health status, and wellness data</li>
                <li>Use the service responsibly and not attempt to exploit or misuse AI features</li>
                <li>Not share your account credentials with others</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Understand that IL scores are educational estimates, not medical measurements</li>
                <li>Seek professional medical care for health concerns</li>
                <li>Respect the privacy and wellbeing of any linked partners</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">5. Age Requirements & Parental Consent</h2>
              <p>
                Users must be at least 13 years old to use Therappi<sup>®</sup>. Users aged 13-17 require parental consent in most jurisdictions. 
                Age verification is conducted during registration. Parents/guardians of minor users are responsible for monitoring their usage.
              </p>
            </section>

            <section className="mb-8 bg-secondary/30 p-6 rounded-lg border border-secondary/50">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Heart className="h-6 w-6 text-primary" />
                6. Partner Linking & Connected Couples
              </h2>
              
              <h3 className="text-xl font-semibold mb-3">6.1 Consent Requirements</h3>
              <p>By sending or accepting a partner link invitation, you:</p>
              <ul className="list-disc pl-6 mb-4">
                <li>Consent to share your email address with the other party for identification</li>
                <li>Consent to share limited wellness data as described in our Privacy Policy</li>
                <li>Acknowledge that your partner may see relationship exercise completions and shared reflections</li>
                <li>Understand that either party may unlink at any time without the other's permission</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">6.2 Acceptable Use</h3>
              <p>You agree NOT to:</p>
              <ul className="list-disc pl-6 mb-4">
                <li>Send partner invitations to harass, stalk, or contact someone who has blocked you</li>
                <li>Create multiple accounts to circumvent being declined</li>
                <li>Use partner features to monitor or control another person</li>
                <li>Share a partner's data with third parties without their explicit consent</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">6.3 Unlinking & Data</h3>
              <p>
                When you unlink from a partner:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Access is severed immediately</li>
                <li>Previously shared exercise history remains in both individual accounts</li>
                <li>Neither party can access the other's ongoing data after unlinking</li>
                <li>Invitation records are retained for 90 days for abuse prevention</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">
                If you experience harassment through partner features, contact us immediately at <a href="mailto:support@therappi.app" className="text-primary hover:underline">support@therappi.app</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">7. Third-Party Integrations</h2>
              
              <h3 className="text-xl font-semibold mb-3">7.1 WHOOP Integration</h3>
              <p>When you connect WHOOP:</p>
              <ul className="list-disc pl-6 mb-4">
                <li>We access HRV, recovery, sleep, and activity data from your WHOOP account</li>
                <li>WHOOP's Terms of Service and Privacy Policy apply to their data collection</li>
                <li>We encrypt and securely store WHOOP access tokens</li>
                <li>You can disconnect WHOOP at any time from Settings → Integrations</li>
                <li>WHOOP data is used to enhance IL score calculations (optional HRV component)</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">7.2 Other Integrations</h3>
              <p>
                Future integrations (Garmin, Oura, etc.) are subject to their respective terms and privacy policies. 
                We are not responsible for the data practices of third-party providers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">8. Data Usage & AI Processing</h2>
              <p>By using our service, you consent to:</p>
              <ul className="list-disc pl-6 mb-4">
                <li>AI analysis of your health and wellness data for personalised insights</li>
                <li>Automated computation of IL scores based on your lifestyle inputs</li>
                <li>Pattern detection and trend analysis across your tracked metrics</li>
                <li>Anonymous aggregate data usage for service improvement and research</li>
                <li>AI-assisted relationship exercises (only if you choose to use partner features)</li>
              </ul>
              <p className="text-sm text-muted-foreground">
                See our Privacy Policy for detailed information on data processing, storage, and your rights.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">9. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, Therappi<sup>®</sup> and its creators are NOT liable for:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Decisions made based on IL scores or AI-generated insights</li>
                <li>Health outcomes resulting from use or non-use of our service</li>
                <li>Inaccuracies in AI-generated content or wellness recommendations</li>
                <li>Technical failures, data loss, or service interruptions</li>
                <li>Third-party integration failures or data breaches at third-party providers</li>
                <li>Actions taken by linked partners based on shared information</li>
                <li>Relationship outcomes resulting from use of partner features</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">10. Subscription & Payments</h2>
              <p>
                Subscription tiers, pricing, and billing terms are subject to change. 
                Refund policies and cancellation procedures are available in your account settings.
                All prices are displayed in GBP unless otherwise stated.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">11. Termination</h2>
              <p>
                We reserve the right to suspend or terminate accounts that violate these Terms, engage in abusive behaviour, 
                misuse partner features to harass others, or misuse the service. You may delete your account at any time from Settings → Privacy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">12. Changes to Terms</h2>
              <p>
                We may update these Terms of Service to reflect changes in our service or legal requirements. 
                Material changes will be communicated via email or in-app notification.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">13. Governing Law</h2>
              <p>
                These Terms are governed by the laws of England and Wales. Disputes will be resolved in English courts.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">14. Contact</h2>
              <p>
                For questions about these Terms, contact us at: <a href="mailto:support@therappi.app" className="text-primary hover:underline">support@therappi.app</a>
              </p>
            </section>

            <section className="bg-card border border-border p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3 flex items-center gap-2 text-primary">
                <Sparkles className="h-5 w-5 text-therapeutic-gold" />
                Our Commitment
              </h3>
              <p>
                We're dedicated to providing safe, ethical, evidence-informed wellness support. 
                These Terms protect both you and us as we work together towards your wellbeing goals.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
